import { Modal, Tooltip } from "@mui/material";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BeatLoader } from "react-spinners";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { useSegmentPalettes } from "../../../../hooks/useSegmentPalettes";
import { getMood, getScheme } from "../../../../redux/selectors/colorPalette";
import {
  getSegmentColorPalette,
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { colors as Colors } from "../../../../utils";
import { getRandomId } from "../../../../utils/helpers";
import Button from "../../../Button";
import TextView from "../../../TextView";
import ColorPaletteModal from "./ColorPaletteModal";
import { SEGMENT_PALETTE_REGEN_ENABLED } from "./constants";
import * as Styled from "./styles";

function transformPaletteData(data, segmentColor, previousPalette) {
  return data.map((item, i) => ({
    index: i,
    paletteName: item.palette_name,
    segmentColor,
    colors: item.colors.map((color, ind) => ({
      hex: color.hex.value,
      id: getRandomId(),
      selected: false,
      locked:
        i == previousPalette?.index
          ? previousPalette?.colors[ind]?.locked
          : false || ind === 0, // Lock the first color which is the segment color
      index: ind,
    })),
    id: getRandomId(),
    selected: i === 0,
  }));
}

export default function ColorPalette({
  title = "Color Palette",
  handleSelectColor = () => {},
  disabled,
  generateAutomatically = false,
  selectedColor,
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getPaletteColor } = usePaletteColor();

  // Gordon ruling 2026-09-15: the Segment Palette tab itself (viewing/
  // selecting the segment's own derived colors, which blocks match against —
  // see the "blocks default to the segment colour + auto-contrast" law) is
  // free for EVERY tier, unlike its sibling Wheel Palette / Custom tabs. Only
  // the deliberate AI "Regenerate" reroll below (a real generate-scheme paid
  // call, gated Essential+) still checks isEligible — do not resurrect a
  // full-tab eligibility gate here (that was the D-16/pre-D-16 bug shape,
  // just inverted: those failed OPEN by accident on an unmapped feature key;
  // this is intentionally, permanently open by product ruling).
  const { isEligible } = useIfUserEligible(FEATURES.ADV_PALLETE_GENERATOR);

  const selectedArc = useSelector(getSelectedSegment) || {};
  const wheelId = useSelector(getWheelId);
  const mood = useSelector(getMood);
  const scheme = useSelector(getScheme);

  const { settings: segmentSettings = {} } = selectedArc;
  const { color: segmentColor } = segmentSettings;
  const { fetchSegmentPalette, regenerateSegmentPalette } =
    useSegmentPalettes();

  // Double-fire guard for the manual Regenerate control: while a deliberate
  // reroll is in flight we disable the button so a user can't fire N parallel
  // (paid) generate-scheme calls by mashing it. Each click = exactly one call.
  const [isRegenerating, setIsRegenerating] = useState(false);

  const toggleModalVisibility = () => setIsModalVisible(!isModalVisible);

  const dispatch = useDispatch();
  const selectedPalette = useSelector(getSegmentColorPalette);
  const colors = selectedPalette ? [...selectedPalette?.colors] : [];

  // Manual "Regenerate" — the explicit, deliberate reroll (PR #1711 / PAL-002).
  // Forces a single fresh harmonizing palette for the segment's current color
  // via the AI generate-scheme endpoint (one call per click). Guarded so it
  // never fires while one is already pending, while ineligible, or with no
  // segment/color selected. Behind SEGMENT_PALETTE_REGEN_ENABLED (default OFF).
  const handleRegenerate = async () => {
    if (
      isRegenerating ||
      !isEligible ||
      !segmentColor ||
      !selectedArc?.id
    )
      return;
    setIsRegenerating(true);
    try {
      await regenerateSegmentPalette(segmentColor, selectedArc?.id);
    } finally {
      setIsRegenerating(false);
    }
  };

  useEffect(() => {
    if (
      (!selectedPalette ||
        selectedPalette?.colors[0]?.hex?.toUpperCase() !==
          segmentColor?.toUpperCase()) &&
      generateAutomatically &&
      segmentColor &&
      selectedArc?.id &&
      isEligible
    )
      fetchSegmentPalette(segmentColor, selectedArc?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentColor, selectedArc?.id, isEligible]);

  return (
    <Styled.Container>
      {!disabled && (
        <div
          style={{
            backgroundColor: Colors.lightBorder,
            padding: 8,
            borderRadius: 4,
            marginBottom: 20,
          }}
        >
          <TextView>
            *Changing the Palette will automatically update all Blocks using
            those colors!
          </TextView>
        </div>
      )}

      <Styled.Header>
        {disabled && <TextView variant="subtitle">{title}</TextView>}

        <Styled.ColorsWrapper>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <BeatLoader color={Colors.PrimaryPurple} size={14} />
            </div>
          ) : (
            !!selectedPalette && (
              <>
                <TextView
                  variant="largeText"
                  style={{
                    marginBottom: 20,
                    userSelect: "none",
                    WebkitLineClamp: 1,
                  }}
                >
                  {selectedPalette.paletteName || selectedPalette.palette_name}
                </TextView>

                <Styled.CircleRow>
                  {colors.reverse().map((color, index) => {
                    const selected = getPaletteColor(selectedColor) == color.hex;
                
                    return (
                      <Tooltip placement="top" title={color.hex}>
                        <Styled.RegenerateButton
                          onClick={() =>
                            !disabled && handleSelectColor(color.index)
                          }
                          key={index}
                          index={index}
                          visible={true}
                          style={{
                            cursor: disabled ? "default" : "pointer",
                            padding: selected ? 2 : 0,
                          }}
                        >
                          <Styled.Circle
                            style={{ backgroundColor: color.hex }}
                          />
                        </Styled.RegenerateButton>
                      </Tooltip>
                    );
                  })}
                </Styled.CircleRow>
              </>
            )
          )}
        </Styled.ColorsWrapper>
      </Styled.Header>

      {SEGMENT_PALETTE_REGEN_ENABLED && !disabled && !!selectedPalette && (
        <Tooltip
          placement="top"
          title="Generate a fresh palette for this segment's color"
        >
          <Button
            variant={"secondary"}
            onClick={handleRegenerate}
            disabled={isRegenerating}
            style={{
              width: "100%",
              marginTop: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              opacity: isRegenerating ? 0.7 : 1,
              cursor: isRegenerating ? "default" : "pointer",
            }}
          >
            {isRegenerating ? (
              <BeatLoader color={Colors.white} size={10} />
            ) : (
              <>
                <LoopOutlinedIcon style={{ fontSize: 18 }} />
                Regenerate Palette
              </>
            )}
          </Button>
        </Tooltip>
      )}

      <Button
        onClick={toggleModalVisibility}
        style={{
          width: "100%",
          marginTop: 20,
        }}
      >
        Open Segment Palette Generator
      </Button>

      {isModalVisible && (
        <Modal open={isModalVisible} onClose={() => setIsModalVisible(false)}>
          <ColorPaletteModal onClose={() => setIsModalVisible(false)} />
        </Modal>
      )}
    </Styled.Container>
  );
}
